import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import axios from "axios";

export default function GoogleReviewFinderModal({
  open,
  setOpen,
  onSelectLink,
  currentValue = "",
  businessName = "",
}) {
  const [searchQuery, setSearchQuery] = useState(businessName || "");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError("Please enter a business name");
      return;
    }

    setIsSearching(true);
    setError("");
    setSearchResults([]);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_PUBLIC_BACKEND_URL}find_google_place`,
        {
          businessName: searchQuery.trim(),
        }
      );

      if (response.data.success && response.data.results) {
        setSearchResults(response.data.results);
        if (response.data.results.length === 0) {
          setError("No businesses found. Try a more specific search.");
        }
      } else {
        setError(response.data.error || "Failed to search for business");
      }
    } catch (error) {
      console.error("Error searching Google Places:", error);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectResult = (place) => {
    const reviewLink = `https://search.google.com/local/writereview?placeid=${place.place_id}`;
    onSelectLink(reviewLink);
    setOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleUseCurrent = () => {
    if (currentValue) {
      onSelectLink(currentValue);
      setOpen(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900 mb-2"
                    >
                      Find Your Google Review Link
                    </Dialog.Title>
                    <p className="text-sm text-gray-600 mb-4">
                      Don't have your review link? No problem! Just search for
                      your business below and we'll generate it for you.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter your business name
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="e.g., Duke's Donuts"
                            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={handleSearch}
                            disabled={isSearching}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {isSearching ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                <span>Searching...</span>
                              </>
                            ) : (
                              <>
                                <MagnifyingGlassIcon className="h-5 w-5" />
                                <span>Search</span>
                              </>
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          We'll search Google and generate your direct review
                          link automatically.
                        </p>
                      </div>

                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                          <p className="text-sm text-red-800">{error}</p>
                        </div>
                      )}

                      {searchResults.length > 0 && (
                        <div className="border border-gray-200 rounded-md max-h-60 overflow-y-auto">
                          <div className="p-2 bg-gray-50 border-b border-gray-200">
                            <p className="text-sm font-medium text-gray-700">
                              Select your business:
                            </p>
                          </div>
                          {searchResults.map((place, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => handleSelectResult(place)}
                              className="w-full text-left p-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                            >
                              <div className="font-medium text-gray-900">
                                {place.name}
                              </div>
                              {place.formatted_address && (
                                <div className="text-sm text-gray-600 mt-1">
                                  {place.formatted_address}
                                </div>
                              )}
                              {place.rating && (
                                <div className="text-xs text-gray-500 mt-1">
                                  ⭐ {place.rating} rating
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {currentValue && (
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-sm text-gray-600 mb-2">
                            Current review link:
                          </p>
                          <p className="text-sm font-mono text-gray-800 break-all">
                            {currentValue}
                          </p>
                          <button
                            type="button"
                            onClick={handleUseCurrent}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                          >
                            Use this link
                          </button>
                        </div>
                      )}

                      <div className="flex gap-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setOpen(false)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>

                      <div className="mt-4 p-3 bg-blue-50 rounded-md">
                        <p className="text-sm text-blue-800">
                          <strong>Why this matters:</strong> This creates a
                          one-tap 5-star experience. Customers can leave a
                          review directly without navigating through multiple
                          pages.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
